import React, { useEffect, useState } from 'react';
import { USE_V2_WEBSOCKET } from '../constant';
import { toast } from 'react-toastify';
import usePaymentWebSocket from '../hooks/usePaymentWebSocket';
import { getFromLocalStorage } from '../hooks/useLocalStorage';
import { useSelector, useDispatch } from 'react-redux';
import { policy } from '../redux/policy/action';
import { Modal, ModalBody, Button } from 'reactstrap';
import './GlobalPaymentListener.scss';

const GlobalPaymentListener = () => {
  const user = getFromLocalStorage("user");
  const userId = user?.user_id;

  const [isListening, setIsListening] = useState(false);

  useEffect(() => {
    const handleStart = () => setIsListening(true);
    const handleStop = () => setIsListening(false);

    window.addEventListener('start-payment-listener', handleStart);
    window.addEventListener('stop-payment-listener', handleStop);

    const handleShowModal = (event) => {
      const { status, message, documentInfo, documentLabel } = event.detail;
      setModalType(status === 'SUCCESS' ? 'success' : 'error');
      setModalMessage(message);
      setModalDocument(documentInfo || '');
      setModalDocumentLabel(documentLabel || 'Document / Ref:');
      setModalOpen(true);
    };
    window.addEventListener('show-global-payment-modal', handleShowModal);

    return () => {
      window.removeEventListener('start-payment-listener', handleStart);
      window.removeEventListener('stop-payment-listener', handleStop);
      window.removeEventListener('show-global-payment-modal', handleShowModal);
    };
  }, []);

  console.log('GlobalPaymentListener rendered!', { userId, USE_V2_WEBSOCKET, isListening });

  // Consume the hook - will only connect if USE_V2_WEBSOCKET, userId, and isListening are truthy
  const { paymentEvent, clearEvent } = usePaymentWebSocket(userId, USE_V2_WEBSOCKET && isListening);

  const dispatch = useDispatch();
  const policyData = useSelector((content) => content.Policy);

  // Modal State
  const [modalOpen, setModalOpen] = useState(false);
  const [modalType, setModalType] = useState('success'); // 'success' or 'error'
  const [modalMessage, setModalMessage] = useState('');
  const [modalDocument, setModalDocument] = useState('');
  const [modalDocumentLabel, setModalDocumentLabel] = useState('Document / Ref:');

  const toggleModal = () => {
    if (modalOpen) {
      // Reload the page when closing the modal to ensure all states are refreshed completely
      window.location.reload();
    } else {
      setModalOpen(true);
    }
  };

  useEffect(() => {
    if (paymentEvent) {
      if (paymentEvent.status === 'SUCCESS' || paymentEvent.status?.toLowerCase() === 'completed') {
        const { origin, documentId, amount } = paymentEvent.payload || {};

        let successMessage = "Your payment was processed successfully!";
        let docInfo = documentId || '';
        let docLabel = 'Document / Ref:';

        if (origin === 'CASHIFY') {
          successMessage = `Your Cashify Policy was generated successfully!`;
        } else if (origin === 'NORMAL_WALLET') {
          successMessage = `Your wallet was successfully recharged!`;
          docInfo = amount ? `₹${amount}` : '';
          docLabel = 'Amount:';
        } else if (origin === 'TRAVEL_CARE_GROUP_CUTNPAY') {
          successMessage = `Your payment was processed successfully!`;
        } else if (origin === 'TRAVEL_CARE') {
          successMessage = `Your Retail Policy was generated successfully!`;
          if (documentId) {
            dispatch(policy({ ...policyData, travellerDetails: { ...policyData.travellerDetails, travelCareMembershipNo: documentId } }));
          }
        }

        setModalType('success');
        setModalMessage(successMessage);
        setModalDocument(docInfo);
        setModalDocumentLabel(docLabel);

        // Dispatch event for other components (like CustomerPaymentConfirmModal) to catch and close
        const event = new CustomEvent('payment-completed', { detail: paymentEvent.payload, cancelable: true });
        const shouldShowGlobalModal = window.dispatchEvent(event);

        if (shouldShowGlobalModal) {
          setModalOpen(true);
        }

      } else if (paymentEvent.status === 'FAILED' || paymentEvent.status?.toLowerCase() === 'failed') {
        const { message } = paymentEvent.payload || {};

        setModalType('error');
        setModalMessage(message || `Payment failed! Please try again.`);
        setModalDocument('');

        const event = new CustomEvent('payment-failed', { detail: paymentEvent.payload, cancelable: true });
        const shouldShowGlobalModal = window.dispatchEvent(event);

        if (shouldShowGlobalModal) {
          setModalOpen(true);
        }
      }

      // Clear the event so it doesn't re-trigger
      clearEvent();
    }
  }, [paymentEvent, clearEvent]);

  if (!USE_V2_WEBSOCKET) {
    return null;
  }

  return (
    <Modal
      isOpen={modalOpen}
      toggle={toggleModal}
      centered
      backdropClassName="payment-listener-modal-backdrop"
      className="payment-listener-modal"
    >
      <ModalBody className="text-center p-5">

        {/* Animated Icon Wrapper */}
        <div className={`payment-status-icon-wrapper ${modalType}`}>
          {modalType === 'success' ? (
            <svg className="checkmark-circle" viewBox="0 0 100 100">
              <circle cx="50" cy="50" r="45" />
              <path className="checkmark-check" d="M30,50 L45,65 L70,35" />
            </svg>
          ) : (
            <svg className="error-circle" viewBox="0 0 100 100">
              <circle cx="50" cy="50" r="45" />
              <path className="error-line" d="M35,35 L65,65 M65,35 L35,65" />
            </svg>
          )}
        </div>

        <h3 className="payment-modal-title">
          {modalType === 'success' ? 'Payment Successful!' : 'Payment Failed'}
        </h3>

        <p className="payment-modal-message">
          {modalMessage}
          {modalDocument && modalType === 'success' && (
            <span className="d-block mt-3">
              {modalDocumentLabel} <span className="payment-modal-document">{modalDocument}</span>
            </span>
          )}
        </p>

        <Button
          color={modalType === 'success' ? 'success' : 'danger'}
          className="payment-modal-btn px-5 mt-2"
          onClick={toggleModal}
        >
          {modalType === 'success' ? 'Continue' : 'Close'}
        </Button>
      </ModalBody>
    </Modal>
  );
};

export default GlobalPaymentListener;
